// LocalEvalStatusBanner.tsx
//
// Renders a progress banner when the operator is running their evaluate
// backlog via web/scripts/local-eval-runner.mjs (laptop-direct, bypasses
// the broker + job-queue path). Polls /api/local-eval-status every 10s;
// when the response says `active: false` the banner hides itself, so
// this component is safe to mount unconditionally on the Matches page.
//
// Status JSON shape (mirrors what local-eval-runner.mjs writes):
//   { active, total, done, failed, in_flight, concurrency,
//     avg_seconds_per_eval, throughput_per_min, eta_seconds,
//     started_at, last_updated, current: [{slot, company, role, ...}] }

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import Progress from '../ui/Progress';
import { apiGet, apiPost } from '../../lib/api';

interface LocalEvalStatus {
  active: boolean;
  finished?: boolean;
  stale?: boolean;
  total?: number;
  done?: number;
  failed?: number;
  in_flight?: number;
  concurrency?: number;
  avg_seconds_per_eval?: number | null;
  throughput_per_min?: number | null;
  eta_seconds?: number | null;
  started_at?: string;
  last_updated?: string;
  current?: Array<{
    slot: number | string;
    company: string;
    role: string;
    url?: string;
    started_at?: string;
  }>;
}

function fmtEta(sec: number | null | undefined): string {
  if (sec == null || sec <= 0) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  const h = sec / 3600;
  if (h < 10) return `${h.toFixed(1)} h`;
  return `${Math.round(h)} h`;
}

export default function LocalEvalStatusBanner() {
  const qc = useQueryClient();
  const [stopRequested, setStopRequested] = useState(false);
  const { data } = useQuery<LocalEvalStatus>({
    queryKey: ['local-eval-status'],
    queryFn: () => apiGet<LocalEvalStatus>('/api/local-eval-status'),
    refetchInterval: 10_000,
    // Don't spam the network on focus/blur — every 10s is plenty.
    refetchOnWindowFocus: false,
  });

  // STOP/RESUME flag — writes a flag file inside the user's userDir.
  // Runner polls /api/local-eval/stop-check every 15s, exits gracefully
  // when flag is set. Resume removes the flag (next runner start picks
  // up fresh; doesn't auto-restart the runner — that's still terminal).
  const stopMut = useMutation({
    mutationFn: (action: 'stop' | 'resume') =>
      apiPost('/api/local-eval/stop', { action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['local-eval-status'] });
    },
  });

  if (!data?.active) return null;

  const total = data.total ?? 0;
  const done = data.done ?? 0;
  const failed = data.failed ?? 0;
  const inFlight = data.in_flight ?? 0;
  const pending = Math.max(0, total - done - failed - inFlight);
  const pct = total > 0 ? Math.round(((done + failed) / total) * 100) : 0;

  const throughput = data.throughput_per_min;
  const eta = data.eta_seconds;
  const avg = data.avg_seconds_per_eval;

  // Headline format mirrors EvaluationBanner so the user's eye finds it
  // in the same place. Distinct prefix ("Local eval") makes it obvious
  // this is the laptop CLI runner, not the SaaS job queue.
  const headline =
    `Local eval · ${done}/${total} done` +
    (inFlight > 0 ? ` · ${inFlight} running` : '') +
    (pending > 0 ? ` · ${pending} waiting` : '') +
    (failed > 0 ? ` · ${failed} failed` : '') +
    (eta != null && eta > 0 ? ` · ~${fmtEta(eta)} left` : '') +
    (throughput != null ? ` (${throughput}/min)` : '');

  return (
    <div
      className="card"
      style={{
        marginBottom: 16,
        padding: '14px 18px',
        background: 'rgba(45,122,74,0.04)',
        border: '1px solid rgba(45,122,74,0.3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Loader2 size={14} color="#2d7a4a" className="spin" />
        <span className="serif" style={{ fontSize: 14, flex: 1 }}>{headline}</span>
        <button
          className="btn"
          style={{
            padding: '4px 10px',
            fontSize: 11,
            color: '#b43a3a',
            opacity: stopMut.isPending || stopRequested ? 0.5 : 1,
          }}
          disabled={stopMut.isPending || stopRequested}
          onClick={() => {
            if (!window.confirm('Stop the laptop eval runner? Workers finish their current evals (~150s each) then exit.')) return;
            setStopRequested(true);
            stopMut.mutate('stop');
          }}
          title="Tells the laptop runner to finish in-flight evals + exit"
        >
          {stopRequested ? 'Stopping…' : 'Stop runner'}
        </button>
      </div>
      <Progress value={pct} color="#2d7a4a" style={{ height: 4, marginBottom: 8 }} />
      {avg != null && (
        <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
          avg {avg}s/eval · concurrency {data.concurrency ?? '?'} · running on laptop (bypasses VM broker)
        </div>
      )}
      {data.current && data.current.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.current.slice(0, 6).map((s) => (
            <div
              key={s.slot}
              style={{
                fontSize: 11,
                color: 'var(--ink2)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={`${s.company} · ${s.role}`}
            >
              <span style={{ color: 'var(--ink3)' }}>slot {s.slot}:</span>{' '}
              <span style={{ color: 'var(--ink)' }}>{s.company}</span>
              {' · '}
              <span style={{ color: 'var(--ink2)' }}>{s.role}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
